import { Link } from "react-router-dom";
import { FileText, ScrollText, Phone, Hospital, AlertTriangle, Scale, Shield, HeartCrack, Landmark, HandCoins } from "lucide-react";
import SEO from "../components/SEO";
import CTASection from "../components/sections/CTASection";

const RESOURCES = [
  {
    icon: AlertTriangle, title: "What to Do After an Accident",
    desc: "A step-by-step guide for the first 48 hours after any Pennsylvania accident. The most important checklist you'll read.",
    link: "/blog/what-to-do-immediately-after-car-accident-pennsylvania",
  },
  {
    icon: ScrollText, title: "Pennsylvania Statute of Limitations",
    desc: "2 years for personal injury in most cases (42 Pa.C.S. § 5524). Different rules apply to medical malpractice, wrongful death, and government claims.",
    link: "/blog/pennsylvania-statute-of-limitations-injury-claims-explained",
  },
  {
    icon: Scale, title: "Pennsylvania Comparative Negligence",
    desc: "How Pennsylvania's 51% bar rule works (42 Pa.C.S. § 7102) — and why insurance companies use it against you.",
    link: "/blog/pennsylvania-comparative-negligence-51-percent-bar-rule-explained",
  },
  {
    icon: Shield, title: "Limited Tort vs. Full Tort",
    desc: "Pennsylvania's 'choice no-fault' election (75 Pa.C.S. § 1705) decides whether you can recover pain and suffering. Know which you have.",
    link: "/blog/limited-tort-vs-full-tort-pennsylvania",
  },
  {
    icon: Hospital, title: "First-Party Benefits (PIP)",
    desc: "How your own auto policy pays medical bills after a crash — regardless of fault — under Pennsylvania's minimum $5,000 first-party medical coverage.",
    link: "/blog/pennsylvania-first-party-benefits-pip-medical-coverage",
  },
  {
    icon: Phone, title: "Talking to Insurance Adjusters",
    desc: "What to say (and not say) to insurance adjusters after a Pennsylvania accident.",
    link: "/blog/dealing-with-insurance-adjusters-after-pa-accident",
  },
  {
    icon: FileText, title: "Settlement Timelines",
    desc: "How long Pennsylvania accident cases really take and what affects speed.",
    link: "/blog/how-long-pennsylvania-car-accident-settlement-take",
  },
  {
    icon: HandCoins, title: "Uninsured / Underinsured Motorist Guide",
    desc: "What happens when the at-fault driver has no insurance — and how UM/UIM coverage protects you.",
    link: "/blog/um-uim-coverage-pennsylvania-uninsured-underinsured-motorists",
  },
  {
    icon: Landmark, title: "Suing a Government Entity",
    desc: "Claims against a city, township, or state agency face a strict 6-month notice rule and sovereign immunity limits. Act fast.",
    link: "/blog/suing-government-entity-pennsylvania-six-month-notice-rule",
  },
  {
    icon: HeartCrack, title: "Pennsylvania Wrongful Death Guide",
    desc: "Who can file, what damages are recoverable, and how the wrongful-death and survival actions differ under Pennsylvania law.",
    link: "/blog/pennsylvania-wrongful-death-claims-beneficiaries-damages",
  },
];

export default function Resources() {
  return (
    <>
      <SEO title="Pennsylvania Accident Resources | Free Guides & Information" description="Free Pennsylvania accident resources, guides, and legal information for victims." />
      <section className="pt-12 md:pt-20 pb-12 px-6 bg-bone">
        <div className="max-w-7xl mx-auto">
          <div className="overline mb-3">Resource Hub</div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-forest tracking-tight mb-5 text-balance max-w-3xl">
            Free Pennsylvania accident resources, guides, and answers.
          </h1>
          <p className="text-lg text-charcoal-soft max-w-2xl">
            Everything we wish every Pennsylvania accident victim knew, in one place.
          </p>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {RESOURCES.map((r, i) => (
            <Link key={i} to={r.link} className="card-elevated p-7 group" data-testid={`resource-card-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-terracotta/10 text-terracotta grid place-items-center mb-4">
                <r.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-xl text-forest mb-2 leading-tight group-hover:text-terracotta transition-colors">
                {r.title}
              </h3>
              <p className="text-sm text-charcoal-soft leading-relaxed">{r.desc}</p>
            </Link>
          ))}
        </div>
      </section>
      <CTASection />
    </>
  );
}
